import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import {
  getAssemblyIssueCommentText,
  isAssemblyIssueComplete,
  normalizeText,
} from './assemblyScheduleConfig';

const getCommentAuthor = (comment) => normalizeText(
  comment?.CreatedBy ??
  comment?.Author ??
  comment?.UserName ??
  comment?.ModifiedBy
);

const formatCommentDate = (comment) => {
  const value = comment?.CreatedDate ?? comment?.DateCreated ?? comment?.ModifiedDate;
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return normalizeText(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const AssemblyIssuesCommentsModal = ({
  show,
  job,
  comments,
  author,
  statusOptions,
  canEdit,
  isSaving,
  updatingCommentId,
  error,
  onHide,
  onAddComment,
  onUpdateStatus,
}) => {
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    if (show) {
      setDescription('');
      setStatus('');
      setShowCompleted(false);
    }
  }, [job?.ID, show]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedDescription = description.trim();
    if (!author || !trimmedDescription || !status) return;
    const saved = await onAddComment({
      Author: author,
      Description: trimmedDescription,
      Status: status,
    });
    if (saved) {
      setDescription('');
      setStatus('');
    }
  };

  const activeComments = comments.filter((comment) => !isAssemblyIssueComplete(comment));
  const completedComments = comments.filter(isAssemblyIssueComplete);
  const visibleComments = showCompleted ? comments : activeComments;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Issues / Comments{job?.JobNumber ? ` — ${job.JobNumber}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="assembly-comments-summary">
          <span>
            {activeComments.length} active
            {completedComments.length > 0 && ` · ${completedComments.length} completed`}
          </span>
          {completedComments.length > 0 && (
            <Form.Check
              type="switch"
              id="assembly-show-completed-comments"
              label="Show completed"
              checked={showCompleted}
              onChange={(event) => setShowCompleted(event.target.checked)}
            />
          )}
        </div>

        <div className="assembly-comments-list" aria-live="polite">
          {visibleComments.length === 0 ? (
            <div className="assembly-comments-empty">
              <i className="bi bi-chat-left-text" aria-hidden="true" />
              <span>
                {comments.length === 0
                  ? 'No issues or comments have been added.'
                  : 'No active issues or comments.'}
              </span>
            </div>
          ) : visibleComments.map((comment, index) => {
            const author = getCommentAuthor(comment);
            const date = formatCommentDate(comment);
            const persistedId = comment?.ID ?? comment?.id;
            const id = persistedId ?? `${job?.ID}-comment-${index}`;
            const currentStatus = comment?.Status ?? comment?.status ?? '';
            const isUpdating = String(updatingCommentId) === String(id);
            return (
              <article className="assembly-comment" key={id}>
                {canEdit && persistedId ? (
                  <Form.Select
                    className="assembly-comment-status-select"
                    size="sm"
                    value={currentStatus}
                    onChange={(event) => onUpdateStatus(comment, event.target.value)}
                    disabled={isSaving || Boolean(updatingCommentId)}
                    aria-label={`Status for comment by ${author || 'unknown author'}`}
                  >
                    {!currentStatus && <option value="">Select status…</option>}
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                ) : currentStatus ? (
                  <Badge bg="secondary" className="assembly-comment-status">
                    {currentStatus}
                  </Badge>
                ) : null}
                <p>{getAssemblyIssueCommentText(comment)}</p>
                {(author || date) && (
                  <small>
                    {author && <strong>{author}</strong>}
                    {author && date && <span aria-hidden="true"> · </span>}
                    {date}
                    {isUpdating && <Spinner animation="border" size="sm" className="ms-2" />}
                  </small>
                )}
              </article>
            );
          })}
        </div>

        {canEdit && (
          <Form className="assembly-comment-form" onSubmit={handleSubmit}>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group controlId="assembly-issue-comment-author">
                  <Form.Label>Author</Form.Label>
                  <Form.Control value={author} readOnly disabled />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="assembly-issue-comment-status">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    disabled={isSaving}
                    required
                  >
                    <option value="">Select status…</option>
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId="assembly-new-issue-comment">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Enter an issue or comment…"
                disabled={isSaving}
                maxLength={4000}
                required
              />
            </Form.Group>
            <div className="assembly-comment-form-actions">
              <Button
                type="submit"
                disabled={isSaving || !author || !description.trim() || !status}
              >
                {isSaving ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Adding…</>
                ) : (
                  <><i className="bi bi-plus-lg me-2" />Add Comment</>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={isSaving || Boolean(updatingCommentId)}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssemblyIssuesCommentsModal;
